import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import useFlowStore from './store'
import { ROUTES } from '@/constants'
import {
  Toolbar,
  ToolbarLeft,
  ToolbarCrumbLink,
  ToolbarCrumbSep,
  ToolbarNameInput,
  ToolbarCenter,
  ToolbarStatusSaved,
  ToolbarStatusDirty,
  ToolbarStatusSaving,
  ToolbarDot,
  ToolbarSpinner,
  ToolbarRight,
  ToolbarSep,
  ToolbarIconBtn,
  ToolbarSaveBtn,
  ToolbarBtn,
  SettingsDropdown,
  SettingsHeader,
  SettingsField,
  SettingsLabel,
  SettingsInput,
  SettingsTextarea,
  SettingsActions,
} from './styles'

interface FlowToolbarProps {
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export default function FlowToolbar({ onSave, onUndo, onRedo, canUndo, canRedo }: FlowToolbarProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Reads directly from store — no prop drilling
  const flow    = useFlowStore((s) => s.flow)
  const saving  = useFlowStore((s) => s.saving)
  const dirty   = useFlowStore((s) => s.dirty)
  const updateFlow = useFlowStore((s) => s.updateFlow)

  // Local state only for the name input (UX: edit without triggering update on every keystroke)
  const [name, setName] = useState(flow?.name ?? '')

  // Syncs when the store changes externally (e.g. initial load)
  useEffect(() => { if (flow?.name !== undefined) setName(flow.name) }, [flow?.name])

  const [inactivityMessage, setInactivityMessage] = useState(flow?.inactivityMessage ?? '')
  const [inactivityTimeout, setInactivityTimeout] = useState(flow?.inactivityTimeoutMinutes ?? 30)
  const [showSettings, setShowSettings] = useState(false)

  // Sync settings fields when flow changes
  useEffect(() => {
    if (!flow) return
    setInactivityMessage(flow.inactivityMessage ?? '')
    setInactivityTimeout(flow.inactivityTimeoutMinutes ?? 30)
  }, [flow?.inactivityMessage, flow?.inactivityTimeoutMinutes])

  const handleNameBlur = () => {
    if (name !== flow?.name) updateFlow({ name })
  }

  const handleSettingsSave = () => {
    updateFlow({ inactivityMessage, inactivityTimeoutMinutes: Number(inactivityTimeout) })
    setShowSettings(false)
  }

  return (
    <Toolbar>
      {/* Left: Back button + breadcrumb + editable name */}
      <ToolbarLeft>
        <ToolbarIconBtn onClick={() => navigate(ROUTES.FLOWS)} title={t('flow.back_to_list')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
        </ToolbarIconBtn>
        <ToolbarCrumbLink onClick={() => navigate(ROUTES.FLOWS)} title={t('flow.back_to_list')}>
          {t('flow.breadcrumb')}
        </ToolbarCrumbLink>
        <ToolbarCrumbSep>/</ToolbarCrumbSep>
        <ToolbarNameInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          placeholder={t('flow.name_placeholder')}
        />
      </ToolbarLeft>

      {/* Center: status de save */}
      <ToolbarCenter>
        {saving ? (
          <ToolbarStatusSaving>
            <ToolbarSpinner /> {t('flow.saving')}
          </ToolbarStatusSaving>
        ) : dirty ? (
          <ToolbarStatusDirty>
            <ToolbarDot $color="#F59E0B" $blink /> {t('flow.unsaved_changes')}
          </ToolbarStatusDirty>
        ) : (
          <ToolbarStatusSaved>
            <ToolbarDot $color="#10B981" /> {t('flow.saved')}
          </ToolbarStatusSaved>
        )}
      </ToolbarCenter>

      {/* Right: actions */}
      <ToolbarRight>
        <ToolbarIconBtn onClick={onUndo} disabled={!canUndo} title={t('flow.undo')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
          </svg>
        </ToolbarIconBtn>
        <ToolbarIconBtn onClick={onRedo} disabled={!canRedo} title={t('flow.redo')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" />
          </svg>
        </ToolbarIconBtn>

        <ToolbarSep />

        <ToolbarIconBtn onClick={() => setShowSettings(!showSettings)} title={t('common.settings')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </ToolbarIconBtn>

        <ToolbarSaveBtn $dirty={dirty} onClick={onSave} disabled={saving}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
          {t('common.save')}
        </ToolbarSaveBtn>
      </ToolbarRight>

      {/* Settings dropdown */}
      {showSettings && (
        <SettingsDropdown>
          <SettingsHeader>{t('flow.inactivity_settings')}</SettingsHeader>
          <SettingsField>
            <SettingsLabel>{t('flow.inactivity_message_label')}</SettingsLabel>
            <SettingsTextarea
              value={inactivityMessage}
              onChange={(e) => setInactivityMessage(e.target.value)}
              placeholder={t('flow.inactivity_message_placeholder')}
              rows={2}
            />
          </SettingsField>
          <SettingsField>
            <SettingsLabel>{t('flow.inactivity_timeout_label')}</SettingsLabel>
            <SettingsInput
              type="number"
              min="1"
              value={inactivityTimeout}
              onChange={(e) => setInactivityTimeout(Number(e.target.value))}
            />
          </SettingsField>
          <SettingsActions>
            <ToolbarBtn onClick={() => setShowSettings(false)}>{t('common.cancel')}</ToolbarBtn>
            <ToolbarSaveBtn onClick={handleSettingsSave}>{t('common.apply')}</ToolbarSaveBtn>
          </SettingsActions>
        </SettingsDropdown>
      )}
    </Toolbar>
  )
}
